import { useMemo } from 'react'
import type { FixtureBuilderOut, FixtureLite, BuilderAngle } from '../types'
import { useOdds } from '../context/OddsContext'
import { angleMarketToOddsMarket } from '../utils'
import { decimalOddsToImpliedProbability } from '../utils/odds'

interface TickerItem {
  market: string // Short display label
  fullLabel: string // Full label for tooltip
  odds: string
  edge: number | null // Edge percentage (conviction - implied)
}

function getEdgeClass(edge: number | null): string {
  if (edge === null) return 'neutral'
  if (edge > 0) return 'up'
  if (edge < -2) return 'down'
  return 'neutral'
}

function formatEdge(edge: number | null): string {
  if (edge === null) return '-'
  const sign = edge >= 0 ? '+' : ''
  return `${sign}${edge.toFixed(1)}%`
}

// Determine team type for market mapping
function getTeamType(
  angle: BuilderAngle,
  homeTeamId: number,
  awayTeamId: number
): 'home' | 'away' | 'total' {
  const entityTeamId = angle.entity?.team_id
  if (entityTeamId === homeTeamId) return 'home'
  if (entityTeamId === awayTeamId) return 'away'
  return 'total'
}

// Get outcome string for odds lookup based on angle
function getOutcomeForAngle(angle: BuilderAngle): string {
  const marketId = angle.market_id.toLowerCase()

  // Match result outcomes
  if (marketId.includes('match_result_home') || marketId.includes('dnb_home')) return 'home'
  if (marketId.includes('match_result_away') || marketId.includes('dnb_away')) return 'away'
  if (marketId.includes('match_result_draw')) return 'draw'

  // Double chance
  if (marketId.includes('double_chance_1x')) return '1X'
  if (marketId.includes('double_chance_x2')) return 'X2'
  if (marketId.includes('double_chance_12')) return '12'

  // BTTS
  if (marketId.includes('btts_no') || angle.selection_label?.toLowerCase().includes('no')) return 'no'
  if (marketId.includes('btts')) return 'yes'

  // Over/under - default to over for most angles
  return 'over'
}

// Market type to readable name mapping
const MARKET_TYPE_NAMES: Record<string, string> = {
  'team-corners-over-under': 'Corners',
  'match-corners-over-under': 'Total Corners',
  'team-goals-over-under': 'Goals',
  'match-goals-over-under': 'Total Goals',
  'team-cards-over-under': 'Cards',
  'match-cards-over-under': 'Total Cards',
  'yellow-cards-over-under': 'Yellow Cards',
  'team-shots-over-under': 'Shots',
  'match-shots-over-under': 'Total Shots',
  'team-sot-over-under': 'Shots on Target',
  'match-sot-over-under': 'Total Shots on Target',
  'team-fouls-over-under': 'Fouls',
  'match-fouls-over-under': 'Total Fouls',
  'btts': 'Both Teams to Score',
  'match-winner': 'Match Result',
  'double-chance': 'Double Chance',
  'draw-no-bet': 'Draw No Bet',
  'clean-sheet': 'Clean Sheet',
  'win-to-nil': 'Win to Nil',
  'anytime-goalscorer': 'Anytime Goalscorer',
  'player-booked': 'Player to be Booked',
  'first-half-goals-over-under': '1st Half Goals',
  'first-half-btts': '1st Half BTTS',
}

// Build full label for tooltip
function buildFullLabel(angle: BuilderAngle, outcome: string, line: number | null): string {
  const marketName = MARKET_TYPE_NAMES[angle.market_type || ''] || angle.market_type || 'Market'
  const entityName = angle.entity?.name || angle.entity?.team_name || ''

  // For over/under markets
  if (line !== null && (outcome === 'over' || outcome === 'under')) {
    const prefix = entityName ? `${entityName} ` : ''
    return `${prefix}${marketName} ${outcome === 'over' ? 'Over' : 'Under'} ${line}`
  }

  // For yes/no markets (BTTS)
  if (outcome === 'yes' || outcome === 'no') {
    return `${marketName}: ${outcome === 'yes' ? 'Yes' : 'No'}`
  }

  // For match result / other markets
  return angle.selection_label || `${entityName} ${marketName}`
}

// Build short label for display
function buildShortLabel(angle: BuilderAngle, outcome: string, line: number | null): string {
  // Get entity short name
  const entityName = angle.entity?.name || angle.entity?.team_name || ''
  const shortEntity = entityName.length > 12 ? entityName.slice(0, 10) + '..' : entityName

  // Short market names
  const shortMarketNames: Record<string, string> = {
    'team-corners-over-under': 'Corners',
    'match-corners-over-under': 'Corners',
    'team-goals-over-under': 'Goals',
    'match-goals-over-under': 'Goals',
    'team-cards-over-under': 'Cards',
    'match-cards-over-under': 'Cards',
    'yellow-cards-over-under': 'Cards',
    'team-shots-over-under': 'Shots',
    'match-shots-over-under': 'Shots',
    'team-sot-over-under': 'SOT',
    'match-sot-over-under': 'SOT',
    'team-fouls-over-under': 'Fouls',
    'match-fouls-over-under': 'Fouls',
    'btts': 'BTTS',
    'match-winner': '',
    'double-chance': 'DC',
    'draw-no-bet': 'DNB',
    'clean-sheet': 'CS',
    'win-to-nil': 'WTN',
    'anytime-goalscorer': 'GS',
    'player-booked': 'Booked',
    'first-half-goals-over-under': '1H Goals',
    'first-half-btts': '1H BTTS',
  }

  const shortMarket = shortMarketNames[angle.market_type || ''] ?? ''

  // For over/under markets with lines
  if (line !== null && (outcome === 'over' || outcome === 'under')) {
    const prefix = shortEntity ? `${shortEntity} ` : ''
    const dir = outcome === 'over' ? 'O' : 'U'
    return `${prefix}${shortMarket} ${dir}${line}`
  }

  // For yes/no markets (BTTS)
  if (outcome === 'yes' || outcome === 'no') {
    return `${shortMarket} ${outcome === 'yes' ? 'Yes' : 'No'}`
  }

  // For match result and other markets, use selection_label but shorten if needed
  const label = angle.selection_label || `${shortEntity} ${shortMarket}`
  return label
}

interface TickerProps {
  fixture?: FixtureLite | null
  builder?: FixtureBuilderOut | null
}

export function Ticker({ fixture, builder }: TickerProps) {
  const { getBestOddsForSelection, formatOdds } = useOdds()

  // Build ticker items from builder angles with their odds and edge
  const items = useMemo(() => {
    if (!fixture || !builder) return []

    const homeTeamId = fixture.home_team.id
    const awayTeamId = fixture.away_team.id
    const tickerItems: TickerItem[] = []

    // Process team angles (goals, corners, cards, etc.)
    const allAngles = [...(builder.angles_team || []), ...(builder.angles_player || [])]

    allAngles.forEach((angle) => {
      if (!angle.market_type) return

      const teamType = getTeamType(angle, homeTeamId, awayTeamId)
      const oddsMarketSlug = angleMarketToOddsMarket(angle.market_type, teamType)

      if (!oddsMarketSlug) return

      // Get the primary line and conviction
      const primaryLine = angle.primary_line
      const conviction = angle.conviction_pct

      // Determine outcome for odds lookup
      const outcome = getOutcomeForAngle(angle)

      // Get best odds for this selection
      const bestOdds = getBestOddsForSelection(
        fixture.id,
        oddsMarketSlug,
        outcome,
        primaryLine
      )

      if (!bestOdds || !bestOdds.odds) return

      // Calculate edge
      const impliedProb = decimalOddsToImpliedProbability(bestOdds.odds)
      const edge = conviction - impliedProb

      // Build full label with line and outcome info
      const fullLabel = buildFullLabel(angle, outcome, primaryLine)

      // Build short display label
      let displayLabel = buildShortLabel(angle, outcome, primaryLine)
      if (displayLabel.length > 28) {
        displayLabel = displayLabel.slice(0, 25) + '...'
      }

      tickerItems.push({
        market: displayLabel,
        fullLabel,
        odds: formatOdds(bestOdds.odds),
        edge,
      })
    })

    // Limit to first 12 items and duplicate for seamless scroll
    const limited = tickerItems.slice(0, 12)
    return [...limited, ...limited]
  }, [fixture, builder, getBestOddsForSelection, formatOdds])

  // Show empty state if no fixture selected or no items
  if (items.length === 0) {
    return (
      <div className="ticker">
        <div className="ticker-track">
          <div className="ticker-item">
            <span className="ticker-market">Select a fixture to see odds</span>
          </div>
        </div>
      </div>
    )
  }

  const fixtureLabel = fixture
    ? `${fixture.home_team.name.slice(0, 3).toUpperCase()} vs ${fixture.away_team.name.slice(0, 3).toUpperCase()}`
    : ''

  return (
    <div className="ticker">
      <div className="ticker-track">
        {/* Live indicator with fixture name */}
        <div className="ticker-item">
          <span className="ticker-live">
            <span className="ticker-live-dot" />
            {fixtureLabel}
          </span>
        </div>
        <div className="ticker-divider" />
        {items.map((item, index) => (
          <div key={`${item.market}-${index}`} className="ticker-item" title={item.fullLabel}>
            <span className="ticker-market">{item.market}</span>
            <span className="ticker-value">{item.odds}</span>
            <span className={`ticker-change ${getEdgeClass(item.edge)}`}>
              {formatEdge(item.edge)}
            </span>
            {index < items.length - 1 && <div className="ticker-divider" />}
          </div>
        ))}
      </div>
    </div>
  )
}

// Ticker that shows actual fixture odds
interface FixtureTickerProps {
  fixtures: Array<{
    id: number
    home_team: { short_name?: string; name: string }
    away_team: { short_name?: string; name: string }
    odds_1?: number
    odds_x?: number
    odds_2?: number
  }>
}

export function FixtureTicker({ fixtures }: FixtureTickerProps) {
  const items = useMemo(() => {
    const tickerItems: Array<{ match: string; odds: string; type: '1' | 'X' | '2' }> = []

    fixtures.slice(0, 10).forEach(f => {
      const home = f.home_team.short_name || f.home_team.name.slice(0, 3).toUpperCase()
      const away = f.away_team.short_name || f.away_team.name.slice(0, 3).toUpperCase()
      const match = `${home} vs ${away}`

      if (f.odds_1) tickerItems.push({ match, odds: f.odds_1.toFixed(2), type: '1' })
      if (f.odds_x) tickerItems.push({ match, odds: f.odds_x.toFixed(2), type: 'X' })
      if (f.odds_2) tickerItems.push({ match, odds: f.odds_2.toFixed(2), type: '2' })
    })

    // Duplicate for seamless scroll
    return [...tickerItems, ...tickerItems]
  }, [fixtures])

  if (items.length === 0) return null

  return (
    <div className="ticker">
      <div className="ticker-track">
        <div className="ticker-item">
          <span className="ticker-live">
            <span className="ticker-live-dot" />
            LIVE ODDS
          </span>
        </div>
        <div className="ticker-divider" />
        {items.map((item, index) => (
          <div key={`${item.match}-${item.type}-${index}`} className="ticker-item">
            <span className="ticker-market">{item.match}</span>
            <span className="ticker-value">{item.type}: {item.odds}</span>
            {index < items.length - 1 && <div className="ticker-divider" />}
          </div>
        ))}
      </div>
    </div>
  )
}
